import React, { useEffect, useState, useRef } from 'react';
import { FaFacebook, FaInstagram, FaTiktok } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VehicleCard from '../components/VehicleCard';
import { getVehiclesPublic, getSiteConfig } from '../services/api';
import { useScrollRestore } from '../hooks/useScrollRestore';

function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [vendus, setVendus] = useState([]);
  const [reviewVideos, setReviewVideos] = useState([]);
  const [tiktokThumbs, setTiktokThumbs] = useState({});
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showTri, setShowTri] = useState(false);
  const [tri, setTri] = useState('nouveautes');
  const [avisPopup, setAvisPopup] = useState({ open: false, url: null });
  const [avisPlaying, setAvisPlaying] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;
  const [filters, setFilters] = useState({
    etat: '',
    carburant: '',
    transmission: '',
    marque: '',
    modele: '',
    anneeMin: '',
    moisMin: '',
    anneeMax: '',
    moisMax: '',
    prixMin: '',
    prixMax: '',
  });

  useEffect(() => {
    getVehiclesPublic()
      .then(res => {
        const enLigne = res.data.filter(v => v.statut === 'EN_LIGNE');
        setVehicles(enLigne);
        setVendus(res.data.filter(v => v.statut === 'VENDU').slice(0, 6));
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));

    getSiteConfig()
      .then(res => {
        setConfig(res.data);
        try { setReviewVideos(res.data.reviewVideoUrls ? JSON.parse(res.data.reviewVideoUrls) : []); } catch { setReviewVideos([]); }
      })
      .catch(err => console.error(err));
  }, []);

  useScrollRestore(loading);

  useEffect(() => {
    reviewVideos.forEach(url => {
      if (!url) return;
      const tiktok = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
      if (tiktok && !tiktokThumbs[url]) {
        fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`)
          .then(r => r.json())
          .then(data => { if (data.thumbnail_url) setTiktokThumbs(prev => ({ ...prev, [url]: data.thumbnail_url })); })
          .catch(() => {});
      }
    });
  }, [reviewVideos]);

  const marques = [...new Set(vehicles.map(v => v.marque).filter(Boolean))];
  const modeles = [...new Set(vehicles.filter(v => !filters.marque || v.marque === filters.marque).map(v => v.modele).filter(Boolean))].sort();

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ etat: '', carburant: '', transmission: '', marque: '', modele: '', anneeMin: '', moisMin: '', anneeMax: '', moisMax: '', prixMin: '', prixMax: '' });
    setSearch('');
  };

  const triOptions = [
    { value: 'nouveautes', label: 'Nouveautés' },
    { value: 'prix-asc', label: 'Prix croissant' },
    { value: 'prix-desc', label: 'Prix décroissant' },
    { value: 'promo', label: 'Promotions d\'abord' },
  ];

  const filtered = vehicles.filter(v => {
    const searchLower = search.toLowerCase();
    const matchSearch = !search ||
      (v.marque && v.marque.toLowerCase().includes(searchLower)) ||
      (v.modele && v.modele.toLowerCase().includes(searchLower)) ||
      (v.carburant && v.carburant.toLowerCase().includes(searchLower)) ||
      (v.couleur && v.couleur.toLowerCase().includes(searchLower)) ||
      (v.transmission && v.transmission.toLowerCase().includes(searchLower));

    const matchEtat = !filters.etat || v.etat === filters.etat;
    const matchCarburant = !filters.carburant || v.carburant === filters.carburant;
    const matchTransmission = !filters.transmission || v.transmission === filters.transmission;
    const matchMarque = !filters.marque || v.marque === filters.marque;
    const matchModele = !filters.modele || v.modele === filters.modele;
    const dateMatch = v.dateMiseEnCirculation ? String(v.dateMiseEnCirculation).match(/(\d{2})\/(\d{2})\/(\d{4})/) : null;
    const moisVehicule = dateMatch ? parseInt(dateMatch[2]) : null;
    const anneeVehicule = dateMatch ? parseInt(dateMatch[3]) : null;
    const dateValeur = (anneeVehicule !== null && moisVehicule !== null) ? anneeVehicule * 100 + moisVehicule : null;
    const dateMin = filters.anneeMin ? parseInt(filters.anneeMin) * 100 + (filters.moisMin ? parseInt(filters.moisMin) : 1) : null;
    const dateMax = filters.anneeMax ? parseInt(filters.anneeMax) * 100 + (filters.moisMax ? parseInt(filters.moisMax) : 12) : null;
    const matchAnneeMin = !dateMin || (dateValeur !== null && dateValeur >= dateMin);
    const matchAnneeMax = !dateMax || (dateValeur !== null && dateValeur <= dateMax);
    const matchPrixMin = !filters.prixMin || v.prix >= parseInt(filters.prixMin);
    const matchPrixMax = !filters.prixMax || v.prix <= parseInt(filters.prixMax);

    return matchSearch && matchEtat && matchCarburant && matchTransmission &&
      matchMarque && matchModele && matchAnneeMin && matchAnneeMax && matchPrixMin && matchPrixMax;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (tri === 'prix-asc') return (a.prixPromo || a.prix || 0) - (b.prixPromo || b.prix || 0);
    if (tri === 'prix-desc') return (b.prixPromo || b.prix || 0) - (a.prixPromo || a.prix || 0);
    if (tri === 'promo') return (b.prixPromo ? 1 : 0) - (a.prixPromo ? 1 : 0);
    return (a.position ?? 9999) - (b.position ?? 9999);
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [1];
    if (current > 4) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 3) pages.push('...');
    pages.push(total);
    return pages;
  };

  const scrollToVehicles = () => {
    document.getElementById('vehicules-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goToPage = (p) => {
    setPage(p);
    scrollToVehicles();
  };

  return (
    <div>
      <Header />

      <section className="relative h-screen overflow-hidden">
        {config && config.heroVideoUrl ? (
          <video src={config.heroVideoUrl} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-black" />
        )}
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-6xl md:text-8xl font-extrabold tracking-widest uppercase mb-4">
            <style>{`
              @keyframes letterPop {
                0%   { opacity: 0; transform: translateY(-20px) scale(0.8); }
                100% { opacity: 1; transform: translateY(0) scale(1); }
              }
            `}</style>
            <div>
              {'ROYAL'.split('').map((l, i) => (
                <span key={i} className="inline-block text-white"
                  style={{ animation: `letterPop 0.25s ease forwards`, animationDelay: `${i * 0.05}s`, opacity: 0 }}>
                  {l}
                </span>
              ))}
            </div>
            <div>
              {'CARS'.split('').map((l, i) => (
                <span key={i} className="inline-block text-yellow-500"
                  style={{ animation: `letterPop 0.25s ease forwards`, animationDelay: `${(5 + i) * 0.05}s`, opacity: 0 }}>
                  {l}
                </span>
              ))}
            </div>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-8">
            {config ? config.headerDescription : 'Votre spécialiste en véhicules de qualité'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#vehicules" className="bg-yellow-500 text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition">
              Voir nos véhicules
            </a>
            <a href="#avis-clients" className="flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-black transition">
              <span className="text-yellow-400">★★★★★</span>
              Avis clients
            </a>
          </div>
        </div>
      </section>

      <section id="vehicules" className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-black text-4xl font-bold mb-2">
          Nos <span className="text-yellow-500">véhicules</span>
        </h2>
        <p className="text-gray-400 mb-8">Trouvez le véhicule qui vous correspond</p>

        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par marque, modèle, carburant..."
              className="w-full border-2 border-gray-200 rounded-xl px-5 py-3 focus:outline-none focus:border-yellow-500 transition text-black"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black">
                x
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-6 py-3 rounded-xl font-bold border-2 transition ${
              showFilters ? 'bg-yellow-500 text-black border-yellow-500' : 'border-gray-200 text-gray-600 hover:border-yellow-500 hover:text-yellow-600'
            }`}
          >
            Filtres
          </button>
          <div className="relative">
            {showTri && <div className="fixed inset-0 z-10" onClick={() => setShowTri(false)} />}
            <button
              onClick={() => setShowTri(!showTri)}
              className={`px-6 py-3 rounded-xl font-bold border-2 transition flex items-center gap-2 ${
                tri !== 'nouveautes' ? 'bg-yellow-500 text-black border-yellow-500' : 'border-gray-200 text-gray-600 hover:border-yellow-500 hover:text-yellow-600'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M6 12h12M10 18h4"/></svg>
              Trier
            </button>
            {showTri && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-20 min-w-[200px] overflow-hidden">
                {triOptions.map(opt => (
                  <button key={opt.value} onClick={() => { setTri(opt.value); setShowTri(false); }}
                    className={`w-full text-left px-5 py-3 text-sm font-semibold transition hover:bg-yellow-50 hover:text-yellow-600 ${tri === opt.value ? 'bg-yellow-500 text-black' : 'text-gray-700'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Marque</label>
                <select name="marque" value={filters.marque} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Toutes</option>
                  {marques.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Modèle</label>
                <select name="modele" value={filters.modele} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Tous</option>
                  {modeles.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Etat</label>
                <select name="etat" value={filters.etat} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Tous</option>
                  <option value="Neuf">Neuf</option>
                  <option value="Occasion">Occasion</option>
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Carburant</label>
                <select name="carburant" value={filters.carburant} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Tous</option>
                  <option value="Essence">Essence</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Electrique">Electrique</option>
                  <option value="Hybride">Hybride</option>
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Transmission</label>
                <select name="transmission" value={filters.transmission} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Toutes</option>
                  <option value="Manuelle">Manuelle</option>
                  <option value="Automatique">Automatique</option>
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Mise en circ. depuis</label>
                <div className="flex gap-2">
                  <select name="moisMin" value={filters.moisMin} onChange={handleFilterChange}
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:border-yellow-500 text-black text-sm">
                    <option value="">Mois</option>
                    {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
                      <option key={i+1} value={String(i+1).padStart(2,'0')}>{m}</option>
                    ))}
                  </select>
                  <input type="number" name="anneeMin" value={filters.anneeMin} onChange={handleFilterChange}
                    placeholder="Année" min="1990" max="2030"
                    className="w-24 border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:border-yellow-500 text-black text-sm" />
                </div>
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Mise en circ. jusqu'à</label>
                <div className="flex gap-2">
                  <select name="moisMax" value={filters.moisMax} onChange={handleFilterChange}
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:border-yellow-500 text-black text-sm">
                    <option value="">Mois</option>
                    {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
                      <option key={i+1} value={String(i+1).padStart(2,'0')}>{m}</option>
                    ))}
                  </select>
                  <input type="number" name="anneeMax" value={filters.anneeMax} onChange={handleFilterChange}
                    placeholder="Année" min="1990" max="2030"
                    className="w-24 border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:border-yellow-500 text-black text-sm" />
                </div>
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Prix min (€)</label>
                <input type="number" name="prixMin" value={filters.prixMin} onChange={handleFilterChange} placeholder="Ex: 5000" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Prix max (€)</label>
                <input type="number" name="prixMax" value={filters.prixMax} onChange={handleFilterChange} placeholder="Ex: 30000" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>
            </div>
            <div className="flex justify-between items-center mt-4">
              <p className="text-gray-500 text-sm">{sorted.length} véhicule(s) trouvé(s)</p>
              <button onClick={resetFilters} className="text-red-400 hover:text-red-600 font-semibold text-sm transition">
                Réinitialiser les filtres
              </button>
            </div>
          </div>
        )}

        <div id="vehicules-section" />
        {loading ? (
          <div className="text-center text-yellow-500 text-xl py-20">Chargement...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            <p className="text-2xl mb-2">Aucun véhicule trouvé</p>
            <p className="text-sm">Essayez de modifier vos critères de recherche</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {paginated.map(vehicle => (
                <div key={vehicle.id} id={`vehicle-${vehicle.id}`}>
                  <VehicleCard vehicle={vehicle} />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12 flex-wrap">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 disabled:opacity-30 hover:border-yellow-500 hover:text-yellow-600 transition font-bold"
                >
                  ‹
                </button>
                {getPageNumbers(page, totalPages).map((p, i) =>
                  p === '...'
                    ? <span key={`e-${i}`} className="w-10 h-10 flex items-center justify-center text-gray-400 text-sm">…</span>
                    : <button
                        key={p}
                        onClick={() => goToPage(p)}
                        className={`w-10 h-10 rounded-full border text-sm font-bold transition ${p === page ? 'bg-yellow-500 border-yellow-500 text-black' : 'border-gray-200 text-gray-600 hover:border-yellow-500 hover:text-yellow-600'}`}
                      >
                        {p}
                      </button>
                )}
                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 disabled:opacity-30 hover:border-yellow-500 hover:text-yellow-600 transition font-bold"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {vendus.length > 0 && (
        <section className="bg-gray-50 border-t border-gray-100 py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-yellow-500 text-sm font-bold uppercase tracking-widest mb-1">Déjà partis</p>
                <h2 className="text-black font-extrabold text-3xl">Véhicules <span className="text-yellow-500">vendus</span></h2>
              </div>
              <a href="/vendus" className="text-gray-500 hover:text-yellow-500 font-semibold text-sm transition flex items-center gap-1">Voir tous les vendus <span className="text-lg">→</span></a>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#eab308 #f3f4f6' }}>
              {vendus.map(v => (
                <a key={v.id} href={`/vehicle/${v.id}`} className="flex-shrink-0 w-64 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group block" style={{ height: '200px' }}>
                  <div className="relative h-full overflow-hidden">
                    {v.mainImageUrl ? <img src={v.mainImageUrl} alt={`${v.marque} ${v.modele}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-gray-800 flex items-center justify-center text-5xl">🚗</div>}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <span className="bg-yellow-500 text-black text-sm font-extrabold px-4 py-2 rounded-xl transform -rotate-12 shadow-lg">VENDU</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <h4 className="font-bold text-white text-sm leading-tight">{v.marque} {v.modele}</h4>
                      <p className="text-gray-300 text-xs mt-1">{v.dateMiseEnCirculation}{v.kilometrage ? ` · ${v.kilometrage.toLocaleString()} km` : ''}</p>
                      <p className="text-gray-300 text-xs mt-0.5">{v.carburant}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Avis clients */}
      <section id="avis-clients" className="bg-white border-t border-gray-100 py-14 px-4" style={{ scrollMarginTop: '80px' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h2 className="text-black font-extrabold text-3xl">
              Avis de nos <span className="text-yellow-500">clients</span>
            </h2>
            <p className="text-gray-500 text-sm font-medium mt-1">Ils nous font confiance</p>
            <div className="flex gap-1 mt-2">
              {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-lg">★</span>)}
            </div>
          </div>
          {reviewVideos.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#eab308 #f3f4f6' }}>
              {reviewVideos.map((url, index) => {
                const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                const isTiktok = url.includes('tiktok.com');
                const isYoutube = !!ytMatch;
                const ytThumb = ytMatch ? `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg` : null;
                const thumb = ytThumb || (isTiktok ? tiktokThumbs[url] : null);
                return (
                  <div key={index}
                    className="relative rounded-2xl overflow-hidden flex-shrink-0 group shadow-md hover:shadow-xl transition-all duration-300 bg-gray-900 block cursor-pointer"
                    style={{ width: '180px', height: '290px' }}
                    onClick={() => { setAvisPopup({ open: true, url }); setAvisPlaying(false); }}>
                    {thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-gray-800 to-gray-900">
                        {isTiktok && (
                          <svg viewBox="0 0 24 24" className="w-12 h-12 text-white opacity-50" fill="currentColor">
                            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                          </svg>
                        )}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 group-hover:from-black/50 transition-all duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center group-hover:bg-yellow-500 group-hover:border-yellow-500 transition-all duration-300 backdrop-blur-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-3 left-3">
                      <span className={`text-white text-xs font-bold px-2 py-1 rounded-full ${isYoutube ? 'bg-red-600' : 'bg-black border border-white/20'}`}>
                        {isYoutube ? 'YouTube' : 'TikTok'}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="text-yellow-400 text-xs font-bold">★★★★★</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-gray-400 text-sm">Aucun avis vidéo pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Popup avis général */}
      {avisPopup.open && (() => {
        const ytMatch = avisPopup.url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        const tiktokMatch = avisPopup.url?.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
        const embedSrc = ytMatch ? `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`
          : tiktokMatch ? `https://www.tiktok.com/embed/v2/${tiktokMatch[1]}` : null;
        const thumb = ytMatch ? `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg` : tiktokThumbs[avisPopup.url] || null;
        const isTiktok = !!tiktokMatch;
        return (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
            onClick={() => { setAvisPopup({ open: false, url: null }); setAvisPlaying(false); }}>
            <div className={`relative flex flex-col items-center w-full ${isTiktok ? 'max-w-sm' : 'max-w-3xl'}`} onClick={e => e.stopPropagation()}>
              <button onClick={() => { setAvisPopup({ open: false, url: null }); setAvisPlaying(false); }}
                className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white text-xl font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition">×</button>
              <div className={`relative w-full rounded-2xl overflow-hidden shadow-2xl bg-gray-900 ${isTiktok ? '' : 'aspect-video'}`}
                style={isTiktok ? { aspectRatio: '9/16', maxHeight: '75vh' } : {}}>
                {avisPlaying ? (
                  <iframe src={embedSrc} title="Avis client"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen className="w-full h-full" />
                ) : (
                  <>
                    {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-900 min-h-[200px]" />}
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center cursor-pointer" onClick={() => setAvisPlaying(true)}>
                      <div className="w-20 h-20 rounded-full bg-white/20 border-2 border-white/80 flex items-center justify-center hover:bg-yellow-500 hover:border-yellow-500 transition-all duration-300 backdrop-blur-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

<section className="py-8 px-4">
  <div className="max-w-7xl mx-auto">
    <div className="rounded-2xl px-10 py-10 text-center" style={{ background: '#0d0d0d' }}>

      <h2 className="text-3xl font-bold text-white mb-3">
        Une question ? <span className="text-yellow-500">Contactez-nous</span>
      </h2>
      <p className="text-gray-400 mb-8">Notre équipe est disponible pour vous accompagner</p>

      <div className="flex justify-center gap-4 flex-wrap">
        {config?.phone && (
          <a href={`tel:${config.phone}`} className="bg-yellow-500 text-black px-8 py-4 rounded-xl font-bold text-lg hover:bg-yellow-400 transition">
            📞 Nous appeler
          </a>
        )}
        {config?.whatsapp && (
          <a href={`https://wa.me/${config.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 border-2 border-green-500 bg-green-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-green-600 transition">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
        )}
        {config?.facebook && (
          <a href={config.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-2 border-2 border-blue-600 bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition">
            <FaFacebook className="w-5 h-5" /> Facebook
          </a>
        )}
        {config?.instagram && (
          <a href={config.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 border-2 border-pink-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition"
            style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
            <FaInstagram className="w-5 h-5" /> Instagram
          </a>
        )}
        {config?.tiktok && (
          <a href={config.tiktok} target="_blank" rel="noreferrer" className="flex items-center gap-2 border-2 border-white bg-black text-white px-8 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition">
            <FaTiktok className="w-5 h-5" /> TikTok
          </a>
        )}
      </div>

    </div>
  </div>
</section>

      {/* Chiffres clés — section blanche séparée */}
      {config && (config.statVehiclesVendus || config.statClientsSatisfaits || config.statAnneesExperience) && (
        <section className="px-4 pb-8">
          <div className="max-w-7xl mx-auto rounded-2xl px-10 py-10 bg-white border border-gray-100 shadow-sm text-center">
            <StatsSection config={config} inline />
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function StatsSection({ config, inline }) {
  const [counts, setCounts] = useState([0, 0, 0]);
  const ref = useRef(null);

  const items = [
    { value: parseInt(config.statVehiclesVendus) || 0, label: 'Véhicules vendus', sub: 'et des clients heureux', icon: '🚗' },
    { value: parseInt(config.statClientsSatisfaits) || 0, label: 'Clients satisfaits', sub: 'France · Algérie · Tunisie', icon: '🤝' },
    { value: parseInt(config.statAnneesExperience) || 0, label: "Années d'expérience", sub: "dans l'automobile", icon: '🏆' },
  ].filter(item => item.value > 0);

  useEffect(() => {
    if (!ref.current || items.length === 0) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const duration = 1500;
      const startTime = performance.now();
      const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setCounts(items.map(item => Math.round(item.value * progress)));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, { threshold: 0.3 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [config]);

  const content = (
    <>
      <div className="mb-6 text-center">
        <p className="text-yellow-500 text-sm font-bold uppercase tracking-[0.3em] mb-2">Royal Cars en chiffres</p>
        <h3 className="text-3xl font-bold text-black">Notre bilan depuis le début</h3>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-6" ref={ref}>
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-4 px-8 py-5 rounded-xl"
            style={{ background: '#1a1a1a', border: '1px solid #c9a84c' }}>
            <span className="text-4xl">{item.icon}</span>
            <div>
              <p className="font-extrabold text-4xl leading-none" style={{ color: '#c9a84c' }}>{counts[i] ?? 0}+</p>
              <p className="text-white text-sm font-bold mt-1">{item.label}</p>
              <p className="text-gray-500 text-xs mt-0.5">{item.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  if (inline) return content;

  return (
    <section className="px-4 pb-6">
      <div className="max-w-7xl mx-auto rounded-2xl px-6 py-8" style={{ background: '#0d0d0d' }}>
        {content}
      </div>
    </section>
  );
}

function useCountUp(target, duration = 1500) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      let start = 0;
      const step = Math.ceil(target / (duration / 16));
      const timer = setInterval(() => {
        start += step;
        if (start >= target) { setCount(target); clearInterval(timer); }
        else setCount(start);
      }, 16);
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);
  return [count, ref];
}

function CounterSection({ vendusCount }) {
  const [c1, ref1] = useCountUp(vendusCount || 150);
  const [c2, ref2] = useCountUp(500);
  const [c3, ref3] = useCountUp(5);

  return (
    <div className="py-16 px-4" style={{ background: 'linear-gradient(135deg, #111 0%, #1a1a1a 50%, #2a2000 100%)' }} ref={ref1}>
      <div className="max-w-5xl mx-auto">
        <p className="text-center text-yellow-500 text-sm font-bold uppercase tracking-widest mb-10">Royal Cars en chiffres</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { count: c1, label: 'Véhicules vendus', sub: 'et des clients heureux', icon: '🚗' },
            { count: c2, label: 'Clients satisfaits', sub: 'France · Algérie · Tunisie', icon: '🤝', ref: ref2 },
            { count: c3, label: "Années d'expérience", sub: "dans l'automobile", icon: '🏆', ref: ref3 },
          ].map((item, i) => (
            <div key={i} ref={item.ref} className="text-center rounded-3xl border border-yellow-500/20 bg-white/5 backdrop-blur-sm p-8 hover:border-yellow-500/50 transition-all duration-300">
              <div className="text-4xl mb-3">{item.icon}</div>
              <p className="text-yellow-400 text-5xl font-extrabold mb-2">{item.count}+</p>
              <p className="text-white font-bold text-lg">{item.label}</p>
              <p className="text-gray-400 text-sm mt-1">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Home;